import AWS from 'aws-sdk';
import { getAwsCredentials } from 'src/authentication/cognito';
import { FlashbarItem } from 'src/components/WhatsNew/FeatureType';
import { FlashbarTemplate } from 'src/components/WhatsNew/FlashbarForm/FlashbarFormTemplates';
import { ReleaseNote } from 'src/components/WhatsNew/WhatsNew';
import { releaseNotesBucketRegion } from 'src/config';
import { logger } from 'src/utils/logger';

const FLASHBAR_ITEMS_KEY = 'releases/flashbar-items.json';


const getS3Instance = async (): Promise<AWS.S3> => {
	await getAwsCredentials();

	const creds = AWS.config.credentials;

	if (!creds || !creds.accessKeyId || !creds.secretAccessKey) {
		throw new Error('Failed to obtain AWS credentials for S3 access');
	}

	return new AWS.S3({
		region: releaseNotesBucketRegion,
		accessKeyId: creds.accessKeyId,
		secretAccessKey: creds.secretAccessKey,
		sessionToken: creds.sessionToken,
		s3ForcePathStyle: true,
		endpoint: `https://s3.${releaseNotesBucketRegion}.amazonaws.com`,
	});
};


export const uploadReleaseNotesToS3 = async (
	releaseData: ReleaseNote,
	year: number,
	month: number,
	bucket: string
): Promise<string> => {
	const s3 = await getS3Instance();
	const fileName = `releases/version.${year}.${month.toString().padStart(2, '0')}.json`;

	const result = await s3.upload({
		Bucket: bucket,
		Key: fileName,
		Body: JSON.stringify(releaseData, null, 2),
		ContentType: 'application/json',
	}).promise();


	return result.Location;
};

export const uploadFlashbarItemsToS3 = async (
	newItem: FlashbarItem,
	bucket: string
): Promise<string> => {
	const s3 = await getS3Instance();

	let items: FlashbarItem[] = [];

	try {
		const { RELEASE_NOTES_CDN_URL } = await import('src/config');
		const response = await fetch(`${RELEASE_NOTES_CDN_URL}/releases/flashbar-items.json?t=${Date.now()}`);

		if (response.ok) {
			items = await response.json();
		}
	} catch {
	}

	const existingIndex = items.findIndex(item => item.id === newItem.id);

	const now = new Date().toISOString();
	items = items.filter((item) => !item.expiresAt || item.expiresAt > now);


	if (existingIndex >= 0) {
		items[existingIndex] = newItem;
	} else {
		items.push(newItem);
	}

	const result = await s3.upload({
		Bucket: bucket,
		Key: FLASHBAR_ITEMS_KEY,
		Body: JSON.stringify(items, null, 2),
		ContentType: 'application/json',
	}).promise();

	return result.Location;
};

export const deleteFlashbarItemFromS3 = async (
	itemId: string,
	bucket: string
): Promise<void> => {
	const s3 = await getS3Instance();

	let items: FlashbarItem[] = [];

	try {
		const { RELEASE_NOTES_CDN_URL } = await import('src/config');
		const response = await fetch(`${RELEASE_NOTES_CDN_URL}/releases/flashbar-items.json?t=${Date.now()}`);

		if (response.ok) {
			items = await response.json();
		}
	} catch {
		return;
	}

	const updatedItems = items.filter(item => item.id !== itemId);

	await s3.upload({
		Bucket: bucket,
		Key: FLASHBAR_ITEMS_KEY,
		Body: JSON.stringify(updatedItems, null, 2),
		ContentType: 'application/json',
	}).promise();
};

export const uploadFlashbarTemplatesToS3 = async (
	templates: FlashbarTemplate[],
	bucket: string
): Promise<void> => {
	const s3 = await getS3Instance();

	await s3.upload({
		Bucket: bucket,
		Key: 'releases/flashbar-templates.json',
		Body: JSON.stringify(templates, null, 2),
		ContentType: 'application/json',
	}).promise();
};


export const uploadCleanedFlashbarItems = async (
	items: FlashbarItem[],
	bucket: string
): Promise<void> => {
	const s3 = await getS3Instance();

	await s3.upload({
		Bucket: bucket,
		Key: FLASHBAR_ITEMS_KEY,
		Body: JSON.stringify(items, null, 2),
		ContentType: 'application/json',
	}).promise();
};

export const getFlashbarItemsFromS3 = async (
	bucket: string
): Promise<FlashbarItem[]> => {
	try {
		const { RELEASE_NOTES_CDN_URL } = await import('src/config');
		const response = await fetch(`${RELEASE_NOTES_CDN_URL}/releases/flashbar-items.json?t=${Date.now()}`);

		if (!response.ok) return [];

		return await response.json();
	} catch (error) {
		logger.error(`Failed to fetch flashbar items [ bucket : error ] ::: [ ${bucket} : ${JSON.stringify(error)} ]`);
		return [];
	}
};

export const getReleaseNoteFromS3 = async (
	year: number,
	month: number,
	bucket: string
): Promise<ReleaseNote | null> => {
	const s3 = await getS3Instance();

	try {
		const data = await s3.getObject({
			Bucket: bucket,
			Key: `releases/version.${year}.${month.toString().padStart(2, '0')}.json`,
		}).promise();

		if (!data.Body) return null;
		const releaseNotes = JSON.parse(data.Body.toString());

		releaseNotes.features.forEach(
			(f: { type: string }) => (f.type = f.type.toLowerCase().replace(/_/g, '-'))
		);

		return releaseNotes;
	} catch (error) {
		const formattedMonth = month.toString().padStart(2, '0');
		logger.error(`Failed to fetch release note from S3 [ year : month : bucket : error ] ::: [ ${year} : ${formattedMonth} : ${bucket} : ${JSON.stringify(error)} ]`);
		return null;
	}
};
